import { Link, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SampleExamDialog from "@/components/SampleExamDialog";

import { TRIAL_EXAM_LABEL } from "@/lib/trialExamConfig";
import { LockedPagePreview } from "@/components/LockedPagePreviews";


/**
 * Locked preview for certification scoped pages.
 *
 * Behind the card we render a static copy of the destination's paid layout.
 * It contains demo values only and mounts no page hooks or user data.
 *
 * This is presentation only. Access control stays server side (RLS).
 */

export const LOCKED_PAGE_COPY: Record<string, { name: string; blurb: string }> = {
  "/dashboard": {
    name: "Dashboard",
    blurb:
      "Track your exam readiness, study time and the next best step to take, updated after every practice session and mock exam.",
  },
  "/insights": {
    name: "Insights",
    blurb:
      "See chapter by chapter mastery, learning objective coverage and the exact questions you keep getting wrong.",
  },
  "/chapters": {
    name: "Chapter Practice",
    blurb:
      "Practice by chapter with full explanations, keyword drills and per chapter mastery scores.",
  },
  "/practice-results": {
    name: "Practice Results",
    blurb: "Review every practice session you have taken, question by question.",
  },
  "/mock-exams": {
    name: "Mock Exams",
    blurb:
      "Take full length, timed mock exams built to the real exam distribution rules, with scoring and review.",
  },
  "/mock-results": {
    name: "Mock Results",
    blurb: "Compare your mock exam attempts over time and see where the points went.",
  },
};

export function lockedCopyForPath(pathname: string) {
  const match = Object.keys(LOCKED_PAGE_COPY).find((p) => pathname.startsWith(p));
  return match
    ? LOCKED_PAGE_COPY[match]
    : { name: "This page", blurb: "Practice, mock exams and progress tracking come with a paid plan." };
}

interface LockedPageOverlayProps {
  pageName: string;
  blurb: string;
}

export const LockedPageOverlay = ({ pageName, blurb }: LockedPageOverlayProps) => {
  const { pathname } = useLocation();

  return (
    <div className="relative h-[calc(100dvh-8rem)] min-h-0 overflow-hidden rounded-lg">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 select-none overflow-hidden p-2 blur-[3px] sm:p-4"
        style={{
          // Fade the preview out at the bottom so rows never end in a hard cut.
          maskImage: "linear-gradient(to bottom, black 65%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 65%, transparent 100%)",
        }}
      >
        <LockedPagePreview pathname={pathname} />
      </div>

      <div className="absolute inset-0 z-10 flex items-center justify-center px-4 py-6">

        <Card className="w-full max-w-xl border border-border/60 bg-card shadow-[0_24px_60px_-12px_hsl(var(--foreground)/0.45)]">
          <CardContent className="px-6 py-8 sm:px-8">
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Lock className="h-6 w-6" />
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
                {pageName}
              </p>
              <h1 className="text-xl font-semibold text-foreground">
                Unlock {pageName} with a paid plan
              </h1>
              <p className="text-sm text-muted-foreground">{blurb}</p>
              <p className="text-sm text-muted-foreground">
                You are on the free plan. Not sure yet? {TRIAL_EXAM_LABEL} first, it takes a
                few minutes and nothing is saved to your progress.
              </p>
              <div className="flex flex-wrap gap-2 justify-center pt-1">
                <Button asChild>
                  <Link to="/pricing?upgrade=1">Upgrade</Link>
                </Button>
                <SampleExamDialog>
                  <Button variant="outline">{TRIAL_EXAM_LABEL}</Button>
                </SampleExamDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

  );


};

export default LockedPageOverlay;
